import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Badge, StatCard, Table, Button, Modal, Input, Select, TextArea } from '../components/Common';

export default function ClientSoW() {
  const { apiFetch, user, hasGroup, isMsp } = useAuth();
  const [sows, setSows] = useState([]);
  const [selectedSow, setSelectedSow] = useState(null);
  const [filter, setFilter] = useState({ client: '', hideExhausted: false });
  const [showDetail, setShowDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // CRUD state (for Billing Admins / Super Admins)
  const [clients, setClients] = useState([]);
  const [showSowModal, setShowSowModal] = useState(false);
  const [sowForm, setSowForm] = useState({});
  const [editingSow, setEditingSow] = useState(null);
  const [sowFile, setSowFile] = useState(null);

  const canManage = hasGroup('MSP Billing Admins') || hasGroup('Client Billing Admins') || hasGroup('Super Admins');

  const load = useCallback(async () => {
    // /client-sow/summary is no longer fetched: the cards are computed from
    // the rows so they follow the filter, and the endpoint always answered for
    // every SoW. It stays on the server for other callers.
    const sowRes = await apiFetch('/client-sow');
    setSows(await sowRes.json());
    if (canManage) {
      try {
        const cliRes = await apiFetch('/clients');
        setClients(await cliRes.json());
      } catch {}
    }
  }, [apiFetch, canManage]);

  useEffect(() => { load(); }, [load]);

  const viewDetail = async (sow) => {
    setSelectedSow(sow);
    try {
      const res = await apiFetch(`/client-sow/${sow.id}`);
      setDetailData(await res.json());
    } catch { setDetailData(null); }
    setShowDetail(true);
  };

  const creditColor = (remaining, total) => {
    if (remaining <= 0) return '#ef4444';
    if (remaining <= Math.ceil(total * 0.2)) return '#f59e0b';
    return '#22c55e';
  };

  // CRUD functions
  const openNewSow = () => {
    setEditingSow(null);
    setSowForm({ status: 'Active', totalCredits: 1 });
    setSowFile(null);
    setShowSowModal(true);
  };

  const openEditSow = (sow) => {
    setEditingSow(sow.id);
    setSowForm({
      clientId: sow.client_id,
      sowNumber: sow.sow_number,
      purchaseOrder: sow.purchase_order || '',
      serviceOrder: sow.service_order || '',
      description: sow.description || '',
      totalCredits: sow.total_credits,
      billingAmount: sow.billing_amount || '',
      startDate: sow.start_date ? sow.start_date.split('T')[0] : '',
      endDate: sow.end_date ? sow.end_date.split('T')[0] : '',
      status: sow.status,
      notes: sow.notes || '',
      existingFile: sow.original_name || null,
      fileSize: sow.file_size || null
    });
    setSowFile(null);
    setShowSowModal(true);
  };

  const saveSow = async () => {
    const method = editingSow ? 'PUT' : 'POST';
    const url = editingSow ? `/billing/sows/${editingSow}` : '/billing/sows';
    const res = await apiFetch(url, {
      method,
      body: JSON.stringify({
        clientId: sowForm.clientId,
        sowNumber: sowForm.sowNumber,
        purchaseOrder: sowForm.purchaseOrder || null,
        serviceOrder: sowForm.serviceOrder || null,
        description: sowForm.description || null,
        totalCredits: parseInt(sowForm.totalCredits) || 1,
        billingAmount: parseFloat(sowForm.billingAmount) || 0,
        startDate: sowForm.startDate || null,
        endDate: sowForm.endDate || null,
        status: sowForm.status || 'Active',
        notes: sowForm.notes || null
      })
    });
    if (!res.ok) {
      const err = await res.json();
      alert(err.error || 'Failed to save SoW');
      return;
    }
    const saved = await res.json();
    const sowId = editingSow || saved.id;

    if (sowFile && sowId) {
      const fd = new FormData();
      fd.append('file', sowFile);
      await apiFetch(`/billing/sows/${sowId}/file`, { method: 'POST', body: fd, isFormData: true });
    }

    setShowSowModal(false);
    setSowForm({});
    setSowFile(null);
    setEditingSow(null);
    load();
  };

  const deleteSow = async (id) => {
    if (!window.confirm('Delete this SoW? This cannot be undone.')) return;
    const res = await apiFetch(`/billing/sows/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const err = await res.json();
      alert(err.error || 'Failed to delete SoW');
      return;
    }
    load();
  };

  const removeSowFile = async (sowId) => {
    if (!window.confirm('Remove the uploaded SoW document?')) return;
    const res = await apiFetch(`/billing/sows/${sowId}/file`, { method: 'DELETE' });
    if (res.ok) {
      setSowForm(f => ({ ...f, existingFile: null, fileSize: null }));
      load();
    } else {
      alert('Failed to remove file');
    }
  };

  const downloadSowFile = async (sowId, fileName) => {
    const res = await apiFetch(`/client-sow/${sowId}/file`);
    if (!res.ok) { alert('Failed to download file'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Clients are taken from the SoWs themselves rather than /clients: it needs no
  // extra fetch, and it cannot offer a client whose only effect would be to empty
  // the table. Only MSP users ever see more than one.
  const clientOptions = Array.from(
    new Map(sows.filter(s => s.client_id).map(s => [s.client_id, s.client_name || '—'])).entries()
  ).sort((a, b) => a[1].localeCompare(b[1]));

  // <= 0 rather than === 0: an over-drawn SoW has nothing left to spend either,
  // and hiding the exhausted ones while leaving the over-drawn ones on screen
  // would be the more surprising reading of "0 credits remaining".
  const visibleSows = sows.filter(s => {
    if (filter.client && s.client_id !== filter.client) return false;
    if (filter.hideExhausted && Number(s.remaining_credits) <= 0) return false;
    return true;
  });
  const isFiltered = visibleSows.length !== sows.length;

  // The cards describe whatever the table is showing. They used to come from
  // /client-sow/summary, which always covered every SoW, so filtering to one
  // client left six totals above the table that belonged to a different set of
  // rows — and the page had to carry a line of small print saying so.
  //
  // Computed from the visible rows, using the same definitions as the server
  // summary so an unfiltered page reads identically to before: counts by
  // status, and credits summed across the SoWs in view. Unfiltered, this is
  // the server's own answer recomputed; filtered, it is the only one that
  // matches what is on screen.
  const visibleSummary = React.useMemo(() => {
    const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
    // Credits can be fractional — engagements.sow_credits is numeric(4,1) —
    // so sums are rounded to one place to keep 35.499999 out of the card.
    const round = (n) => Math.round(n * 10) / 10;
    return {
      total_sows: visibleSows.length,
      active_sows: visibleSows.filter(x => x.status === 'Active').length,
      complete_sows: visibleSows.filter(x => x.status === 'Complete').length,
      total_credits: round(visibleSows.reduce((a, x) => a + num(x.total_credits), 0)),
      used_credits: round(visibleSows.reduce((a, x) => a + num(x.used_credits), 0)),
      remaining_credits: round(visibleSows.reduce((a, x) => a + num(x.remaining_credits), 0)),
    };
  }, [visibleSows]);

  const columns = [
    { key: 'client_name', label: 'Client' },
    { key: 'sow_number', label: 'SoW Number', render: (v, row) => <span style={{ fontWeight: 600 }}>{v} {row.original_name ? '📎' : ''}</span> },
    { key: 'purchase_order', label: 'PO Number', render: v => v || '—' },
    { key: 'service_order', label: 'Service Order', render: v => v || '—' },
    { key: 'description', label: 'Description', render: v => v ? <span style={{ fontSize: 13, maxWidth: 250, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span> : '—' },
    { key: 'billing_amount', label: 'Invoice Amount', render: v => v && parseFloat(v) > 0 ? `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—' },
    { key: 'total_credits', label: 'Total Credits', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'used_credits', label: 'Used', render: v => v || 0 },
    { key: 'remaining_credits', label: 'Remaining', render: (v, row) => (
      <span style={{ fontWeight: 700, color: creditColor(v, row.total_credits) }}>{v}</span>
    )},
    { key: 'status', label: 'Status', render: v => <Badge>{v}</Badge> },
    // Start and End were dropped: they were empty on most rows and the space
    // is better spent on the order references the billing team works from.
    // Both dates are still on the SoW's own detail view and in its edit form.
    { key: 'id', label: 'Actions', render: (v, row) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
          onClick={(e) => { e.stopPropagation(); viewDetail(row); }}>
          View
        </Button>
        {canManage && (
          <>
            <Button variant="secondary" style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={(e) => { e.stopPropagation(); openEditSow(row); }}>Edit</Button>
            <Button variant="danger" style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={(e) => { e.stopPropagation(); deleteSow(v); }}>Delete</Button>
          </>
        )}
      </div>
    )}
  ];

  const engColumns = [
    { key: 'name', label: 'Engagement' },
    { key: 'engagement_type', label: 'Type', render: v => <span style={{ fontSize: 13 }}>{v}</span> },
    { key: 'status', label: 'Status', render: v => <Badge>{v}</Badge> },
    { key: 'sow_credits', label: 'Credits', render: v => parseFloat(v) || 1 },
    { key: 'start_date', label: 'Start', render: v => v ? new Date(v).toLocaleDateString() : '—' },
    { key: 'end_date', label: 'End', render: v => v ? new Date(v).toLocaleDateString() : '—' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Statements of Work</h1>
        {canManage && <Button onClick={openNewSow}>+ New SoW</Button>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total SoWs" value={visibleSummary.total_sows || 0} icon="📑" color="#8b5cf6" />
        <StatCard label="Active" value={visibleSummary.active_sows || 0} icon="✅" color="#22c55e" />
        <StatCard label="Total Credits" value={visibleSummary.total_credits || 0} icon="🎟️" color="#3b82f6" />
        <StatCard label="Used Credits" value={visibleSummary.used_credits || 0} icon="📋" color="#f59e0b" />
        <StatCard label="Remaining Credits" value={visibleSummary.remaining_credits || 0} icon="🎯" color="#06b6d4" />
        <StatCard label="Completed" value={visibleSummary.complete_sows || 0} icon="🏁" color="#6366f1" />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        {isMsp && clientOptions.length > 1 && (
          <select value={filter.client} onChange={e => setFilter(f => ({ ...f, client: e.target.value }))}
            style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-card)', color: filter.client ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">All Clients</option>
            {clientOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 8, background: filter.hideExhausted ? 'rgba(79,143,255,0.08)' : 'var(--bg-card)' }}>
          <input type="checkbox" checked={filter.hideExhausted}
            onChange={e => setFilter(f => ({ ...f, hideExhausted: e.target.checked }))} />
          Hide SoWs with no credits remaining
        </label>

        {isFiltered && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Showing {visibleSows.length} of {sows.length}
            {' '}&mdash; the totals above reflect this filter
          </span>
        )}
      </div>

      <Table columns={columns} data={visibleSows} />

      {/* SoW Detail Modal */}
      <Modal isOpen={showDetail} onClose={() => { setShowDetail(false); setDetailData(null); }}
        title={selectedSow ? `SoW: ${selectedSow.sow_number}` : 'SoW Details'} width={700}>
        {detailData && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
              <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg-input)', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{detailData.total_credits}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Total Credits</div>
              </div>
              <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg-input)', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{detailData.used_credits}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Used</div>
              </div>
              <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg-input)', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: creditColor(detailData.remaining_credits, detailData.total_credits) }}>
                  {detailData.remaining_credits}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Remaining</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20, fontSize: 14 }}>
              <div><span style={{ color: 'var(--text-secondary)' }}>Status:</span> <Badge>{detailData.status}</Badge></div>
              <div><span style={{ color: 'var(--text-secondary)' }}>Client:</span> {detailData.client_name}</div>
              <div><span style={{ color: 'var(--text-secondary)' }}>PO Number:</span> {detailData.purchase_order || '—'}</div>
              <div><span style={{ color: 'var(--text-secondary)' }}>Invoice Amount:</span> {detailData.billing_amount && parseFloat(detailData.billing_amount) > 0 ? `$${parseFloat(detailData.billing_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}</div>
              <div><span style={{ color: 'var(--text-secondary)' }}>Start:</span> {detailData.start_date ? new Date(detailData.start_date).toLocaleDateString() : '—'}</div>
              <div><span style={{ color: 'var(--text-secondary)' }}>End:</span> {detailData.end_date ? new Date(detailData.end_date).toLocaleDateString() : '—'}</div>
            </div>

            {detailData.description && (
              <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: 'var(--bg-input)', fontSize: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Description</div>
                {detailData.description}
              </div>
            )}

            {/* SoW Document */}
            <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: 'var(--bg-input)', fontSize: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>SoW Document</div>
              {detailData.original_name ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>📄</span>
                  <span style={{ flex: 1 }}>{detailData.original_name}</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                    {detailData.file_size ? `${(detailData.file_size / 1024).toFixed(0)} KB` : ''}
                  </span>
                  <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
                    onClick={() => downloadSowFile(detailData.id, detailData.original_name)}>Download</Button>
                </div>
              ) : (
                <span style={{ color: 'var(--text-secondary)' }}>No document uploaded</span>
              )}
            </div>

            {/* Linked engagements */}
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
              Linked Engagements ({detailData.engagements?.length || 0})
            </div>
            {detailData.engagements?.length > 0 ? (
              <Table columns={engColumns} data={detailData.engagements} />
            ) : (
              <Card style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
                No engagements linked to this SoW yet
              </Card>
            )}
          </div>
        )}
      </Modal>

      {/* SoW Create/Edit Modal */}
      <Modal isOpen={showSowModal} onClose={() => { setShowSowModal(false); setEditingSow(null); setSowFile(null); }}
        title={editingSow ? 'Edit Statement of Work' : 'New Statement of Work'} width={560}>
        {!editingSow && (
          <Select label="Client *" value={sowForm.clientId || ''}
            onChange={e => setSowForm(f => ({ ...f, clientId: e.target.value }))}
            options={[{ value: '', label: '— Select Client —' }, ...clients.map(c => ({ value: c.id, label: c.name }))]} />
        )}
        <Input label="SoW Number *" value={sowForm.sowNumber || ''} placeholder="e.g. SOW-2026-001"
          onChange={e => setSowForm(f => ({ ...f, sowNumber: e.target.value }))} />
        <Input label="Purchase Order" value={sowForm.purchaseOrder || ''} placeholder="The client's PO number, if they have raised one"
          onChange={e => setSowForm(f => ({ ...f, purchaseOrder: e.target.value }))} />
        <Input label="Service Order" value={sowForm.serviceOrder || ''} placeholder="e.g. SO-2026-001"
          onChange={e => setSowForm(f => ({ ...f, serviceOrder: e.target.value }))} />
        <Input label="Description" value={sowForm.description || ''} placeholder="Brief description"
          onChange={e => setSowForm(f => ({ ...f, description: e.target.value }))} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Input label="Total Credits" type="number" min="1" value={sowForm.totalCredits || ''}
            onChange={e => setSowForm(f => ({ ...f, totalCredits: e.target.value }))} />
          <Input label="Invoice Amount ($)" type="number" min="0" step="0.01" value={sowForm.billingAmount || ''}
            onChange={e => setSowForm(f => ({ ...f, billingAmount: e.target.value }))} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Input label="Start Date" type="date" value={sowForm.startDate || ''}
            onChange={e => setSowForm(f => ({ ...f, startDate: e.target.value }))} />
          <Input label="End Date" type="date" value={sowForm.endDate || ''}
            onChange={e => setSowForm(f => ({ ...f, endDate: e.target.value }))} />
        </div>
        <Select label="Status" value={sowForm.status || 'Active'}
          onChange={e => setSowForm(f => ({ ...f, status: e.target.value }))}
          options={[
            { value: 'Draft', label: 'Draft' },
            { value: 'Waiting on Client', label: 'Waiting on Client' },
            { value: 'Active', label: 'Active' },
            { value: 'Complete', label: 'Complete' },
            { value: 'Cancelled', label: 'Cancelled' }
          ]} />
        <TextArea label="Notes" value={sowForm.notes || ''} placeholder="Internal notes..."
          onChange={e => setSowForm(f => ({ ...f, notes: e.target.value }))} />

        {/* SoW Document Upload */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>
            SoW Document
          </label>
          {sowForm.existingFile && !sowFile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg-input)', borderRadius: 'var(--radius)', marginBottom: 8, fontSize: 13 }}>
              <span>📄</span>
              <span style={{ flex: 1, fontWeight: 500 }}>{sowForm.existingFile}</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                {sowForm.fileSize ? `${(sowForm.fileSize / 1024).toFixed(0)} KB` : ''}
              </span>
              <Button variant="secondary" style={{ padding: '2px 8px', fontSize: 11 }}
                onClick={() => downloadSowFile(editingSow, sowForm.existingFile)}>Download</Button>
              <Button variant="danger" style={{ padding: '2px 8px', fontSize: 11 }}
                onClick={() => removeSowFile(editingSow)}>Remove</Button>
            </div>
          )}
          <input type="file" onChange={e => setSowFile(e.target.files[0])}
            style={{ fontSize: 13, color: 'var(--text)' }} />
          {sowFile && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              Selected: {sowFile.name} ({(sowFile.size / 1024).toFixed(0)} KB)
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => { setShowSowModal(false); setEditingSow(null); setSowFile(null); }}>Cancel</Button>
          <Button onClick={saveSow} disabled={!editingSow && (!sowForm.clientId || !sowForm.sowNumber)}>
            {editingSow ? 'Update SoW' : 'Create SoW'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
